import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiHome,
  FiMonitor,
  FiFileText,
  FiGlobe,
  FiUserCheck,
  FiBriefcase,
  FiHeart,
  FiUsers,
  FiTruck
} = FiIcons;

const services = [
  {
    icon: FiHome,
    title: 'Mobile Notary',
    description: "We come to your home,office,hospital,or wherever works for you. Bring your ID,we'll bring everything else.",
    portuguese: 'Notário Móvel',
    href: '/services/mobile-notary',
    image: 'https://images.unsplash.com/photo-1556912173-3bb406ef7e77?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiMonitor,
    title: 'Remote Online Notary',
    description: 'Notarize from anywhere with a live video call. Maryland-authorized,available worldwide.',
    portuguese: 'Notário Online',
    href: '/services/remote-online-notary',
    image: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiFileText,
    title: 'General Notarizations',
    description: 'Powers of attorney,affidavits,sworn statements,and everything in between.',
    portuguese: 'Reconhecimento de Firma',
    href: '/services/general-notarizations',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiGlobe,
    title: 'Apostille Services',
    description: 'Documents heading abroad? We handle notarization,certification,and the apostille stamp. Flat $197.',
    portuguese: 'Serviço de Apostila',
    href: '/services/apostille',
    image: 'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiUserCheck,
    title: 'I-9 Employment Verification',
    description: 'Remote hires still need their ID checked in person. We meet your new employee and complete Section 2 of the Form I-9 as your authorized representative.',
    portuguese: 'Verificação I-9 de Emprego',
    href: '/services/i-9-employment-verification',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiBriefcase,
    title: 'Business & Corporate Documents',
    description: 'Corporate resolutions,business agreements,and authorization letters,notarized on-site or at your office.',
    href: '/services/business-corporate',
    image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiHeart,
    title: 'Medical & Healthcare',
    description: 'Advance directives,healthcare proxies,and medical consent forms,handled with discretion and care.',
    href: '/services/medical-healthcare',
    image: 'https://images.unsplash.com/photo-1583912267550-d5a3d9f13d8f?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiUsers,
    title: 'Family & Estate Planning',
    description: 'Wills,trusts,and powers of attorney,notarized with patience and attention to detail.',
    href: '/services/family-estate-planning',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=85'
  },
  {
    icon: FiTruck,
    title: 'Vehicle Documents',
    description: 'Title transfers,bills of sale,and MVA forms,notarized quickly so your transaction goes through.',
    href: '/services/vehicle-documents',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=900&q=85'
  }
];

const UnifiedServices = () => {
  return (
    <section className="py-24 px-6 bg-cream-light">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-brass text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            WHAT WE OFFER
          </span>
          <h2 className="font-fraunces text-4xl md:text-5xl text-navy mb-6">
            Notary services,
            <span className="italic text-brass"> done right.</span>
          </h2>
          <p className="text-lg text-navy/70 leading-relaxed">
            From a single signature to a full apostille,here&apos;s everything we
            handle. Click into any service for details.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.article
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-cream rounded-2xl border border-line shadow-sm overflow-hidden hover:border-brass/40 transition-all group flex flex-col"
            >
              <img
                src={service.image}
                alt=""
                className="w-full h-48 object-cover sepia-[0.12] group-hover:scale-[1.03] transition-transform duration-700"
              />

              <div className="p-8 flex flex-col flex-grow">
                <div className="w-12 h-12 rounded-full border border-brass flex items-center justify-center text-brass mb-6 group-hover:bg-brass group-hover:text-cream transition-colors">
                  <SafeIcon icon={service.icon} size={22} />
                </div>

                <h3 className="font-fraunces text-2xl text-navy mb-4">
                  {service.title}
                </h3>
                <p className="text-navy/70 text-sm leading-relaxed flex-grow">
                  {service.description}
                </p>

                {service.portuguese && (
                  <p className="font-fraunces italic text-brass text-lg mt-6">
                    {service.portuguese}
                  </p>
                )}

                <a
                  href={service.href}
                  className="inline-flex items-center gap-2 text-sm font-bold text-navy mt-6 pt-5 border-t border-dashed border-line hover:text-brass transition-colors"
                >
                  Learn more <span aria-hidden="true">→</span>
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/services"
            className="font-bold text-navy hover:text-brass transition-colors"
          >
            View All Services <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default UnifiedServices;